class SIbuprofenParacetamolToxicityCalculator extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.abortController = null;
    this.state = { species: "dog", drug: "ibuprofen", weightUnit: "kg", mode: "tablets" };
    this.thresholds = {
      ibuprofen: {
        dog: [
          { min: 0, level: "monitor", title: "Below the usual toxic dose", concern: "Below the typical threshold for a single dose, but ibuprofen has a narrow safety margin in dogs and repeated doses can cause stomach ulcers." },
          { min: 25, level: "toxic", title: "Stomach and gut toxicity likely", concern: "This dose can cause vomiting, diarrhoea, appetite loss and stomach ulceration, which may bleed." },
          { min: 175, level: "severe", title: "Kidney injury risk", concern: "At this dose ibuprofen can damage the kidneys and cause acute kidney failure, as well as gut ulceration." },
          { min: 400, level: "critical", title: "Life-threatening brain and kidney effects", concern: "This dose can cause wobbliness, seizures, collapse or coma alongside kidney failure. Treat it as an immediate emergency." }
        ],
        cat: [
          { min: 0, level: "monitor", title: "Below the usual toxic dose", concern: "Below the typical single-dose threshold, but cats are more sensitive than dogs and have very little safety margin." },
          { min: 12.5, level: "toxic", title: "Stomach and gut toxicity likely", concern: "This dose can cause vomiting, diarrhoea, appetite loss and stomach ulceration in a cat." },
          { min: 88, level: "severe", title: "Kidney injury risk", concern: "At this dose ibuprofen can cause acute kidney failure in a cat, as well as gut ulceration." },
          { min: 200, level: "critical", title: "Life-threatening brain and kidney effects", concern: "This dose can cause seizures, collapse or coma with kidney failure. Treat it as an immediate emergency." }
        ]
      },
      paracetamol: {
        dog: [
          { min: 0, level: "monitor", title: "Below the usual toxic dose", concern: "Below the dose where dogs usually show signs, but liver and blood effects are still possible, particularly after repeat dosing or in small or unwell dogs." },
          { min: 100, level: "severe", title: "Liver damage risk", concern: "At this dose paracetamol can injure the liver. Signs may be delayed, so early veterinary advice matters." },
          { min: 200, level: "critical", title: "Life-threatening blood and liver effects", concern: "This dose can impair the blood's ability to carry oxygen and cause serious liver damage. This is a true emergency." }
        ],
        cat: [
          { min: 0, level: "severe", title: "There is no safe dose for a cat", concern: "Cats cannot process paracetamol safely. Even part of a tablet can be dangerous, and signs can appear quickly." },
          { min: 10, level: "critical", title: "Life-threatening for a cat", concern: "This dose can stop the blood carrying oxygen normally and can also damage the liver. Treat it as a dire emergency." },
          { min: 50, level: "critical", title: "Life-threatening for a cat", concern: "This is a very high-risk exposure. Every minute counts." }
        ]
      }
    };
    this.strengths = {
      ibuprofen: [100, 200, 400, 600, 800],
      paracetamol: [160, 325, 500, 650, 1000]
    };
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();
    this.cacheElements();
    this.bindEvents();
    this.renderStrengthChips();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.initialized = false;
  }

  cacheElements() {
    this.form = this.querySelector("[data-ipt-form]");
    this.weightInput = this.querySelector("[data-ipt-weight]");
    this.tabletMode = this.querySelector("[data-ipt-tablet-mode]");
    this.mgMode = this.querySelector("[data-ipt-mg-mode]");
    this.tabletCount = this.querySelector("[data-ipt-tablet-count]");
    this.tabletStrength = this.querySelector("[data-ipt-tablet-strength]");
    this.totalMg = this.querySelector("[data-ipt-total-mg]");
    this.chips = this.querySelector("[data-ipt-strength-chips]");
    this.hint = this.querySelector("[data-ipt-strength-hint]");
    this.error = this.querySelector("[data-ipt-error]");
    this.result = this.querySelector("[data-ipt-result]");
    this.banner = this.querySelector("[data-ipt-banner]");
    this.tag = this.querySelector("[data-ipt-tag]");
    this.resultTitle = this.querySelector("[data-ipt-result-title]");
    this.dose = this.querySelector("[data-ipt-dose]");
    this.meaning = this.querySelector("[data-ipt-meaning]");
    this.signs = this.querySelector("[data-ipt-signs]");
    this.actions = this.querySelector("[data-ipt-actions]");
  }

  bindEvents() {
    const { signal } = this.abortController;
    this.querySelectorAll("[data-ipt-toggle]").forEach((button) => {
      button.addEventListener("click", () => this.handleToggle(button), { signal });
    });
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.calculate();
    }, { signal });
    this.querySelector("[data-ipt-restart]")?.addEventListener("click", () => this.restart(), { signal });
  }

  handleToggle(button) {
    const key = button.dataset.iptToggle;
    const value = button.dataset.value;
    const group = button.closest("[data-ipt-group]");
    if (!key || !value || !group) return;

    if (key === "weightUnit" && value !== this.state.weightUnit) {
      const current = Number.parseFloat(this.weightInput.value);
      if (Number.isFinite(current) && current > 0) {
        const converted = value === "lb" ? current * 2.2046226218 : current / 2.2046226218;
        this.weightInput.value = Number.parseFloat(converted.toFixed(2)).toString();
      }
    }

    this.state[key] = value;
    group.querySelectorAll("[data-ipt-toggle]").forEach((item) => {
      const active = item === button;
      item.classList.toggle("is-active", active);
      item.setAttribute("aria-pressed", String(active));
    });

    if (key === "drug") this.renderStrengthChips();
    if (key === "mode") this.renderMode();
    this.hideResult();
  }

  renderMode() {
    const tabletMode = this.state.mode === "tablets";
    this.tabletMode.hidden = !tabletMode;
    this.mgMode.hidden = tabletMode;
  }

  renderStrengthChips() {
    this.chips.innerHTML = "";
    const defaultStrength = this.state.drug === "ibuprofen" ? 200 : 500;
    this.tabletStrength.value = String(defaultStrength);
    this.strengths[this.state.drug].forEach((strength) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "s-ibuprofen-paracetamol-calculator__chip";
      button.textContent = `${strength} mg${strength === defaultStrength ? " (standard)" : ""}`;
      button.addEventListener("click", () => {
        this.chips.querySelectorAll("button").forEach((item) => item.classList.remove("is-active"));
        button.classList.add("is-active");
        this.tabletStrength.value = String(strength);
        this.hideResult();
      }, { signal: this.abortController.signal });
      this.chips.appendChild(button);
    });
    this.hint.textContent = this.state.drug === "ibuprofen"
      ? "Choose a common tablet strength or enter the milligrams printed on the packet."
      : "Choose a common tablet strength or enter the milligrams on the packet. Cold and flu products may also contain paracetamol.";
  }

  calculate() {
    this.clearError();
    const weight = Number.parseFloat(this.weightInput.value);
    if (!Number.isFinite(weight) || weight <= 0) return this.showError("Enter your pet's weight using a number greater than zero.", this.weightInput);
    if ((this.state.weightUnit === "kg" && weight > 250) || (this.state.weightUnit === "lb" && weight > 550)) {
      return this.showError("Check the entered weight and selected unit. The value looks unusually high.", this.weightInput);
    }
    const weightKg = this.state.weightUnit === "lb" ? weight / 2.2046226218 : weight;

    let totalMg;
    if (this.state.mode === "tablets") {
      const count = Number.parseFloat(this.tabletCount.value);
      const strength = Number.parseFloat(this.tabletStrength.value);
      if (!Number.isFinite(count) || count <= 0) return this.showError("Enter how many tablets were swallowed.", this.tabletCount);
      if (!Number.isFinite(strength) || strength <= 0) return this.showError("Enter the milligrams per tablet.", this.tabletStrength);
      totalMg = count * strength;
    } else {
      totalMg = Number.parseFloat(this.totalMg.value);
      if (!Number.isFinite(totalMg) || totalMg <= 0) return this.showError("Enter the total milligrams swallowed.", this.totalMg);
    }

    if (totalMg > 100000) return this.showError("Check the amount entered. The total milligrams look unusually high.");
    const mgKg = totalMg / weightKg;
    const bands = this.thresholds[this.state.drug][this.state.species];
    let band = bands[0];
    bands.forEach((candidate) => { if (mgKg >= candidate.min) band = candidate; });
    this.renderResult(band, mgKg, totalMg);
  }

  renderResult(band, mgKg, totalMg) {
    const tags = { monitor: "Monitor closely", toxic: "Toxic dose", severe: "Serious — vet now", critical: "Emergency — go now" };
    this.banner.className = `s-ibuprofen-paracetamol-calculator__banner s-ibuprofen-paracetamol-calculator__banner--${band.level}`;
    this.tag.textContent = tags[band.level];
    this.resultTitle.textContent = band.title;
    this.dose.innerHTML = `Estimated dose: <b>${mgKg.toFixed(1)} mg/kg</b> (${Math.round(totalMg)} mg total)`;
    this.meaning.textContent = band.concern;
    this.signs.innerHTML = this.state.drug === "ibuprofen"
      ? "<b>Watch for:</b> vomiting, blood in vomit, black stools, appetite loss, lethargy, changes in drinking or urination, wobbliness or seizures."
      : "<b>Watch for:</b> brown or blue-grey gums, swelling of the face or paws, drooling, fast or laboured breathing, dark urine, lethargy or yellowing of the eyes or skin.";
    const items = band.level === "monitor" ? [
      "Call your vet or a poison line now and provide the exact amount, drug strength and your pet's weight.",
      "Do not give food, milk or home remedies, and do not induce vomiting unless a veterinarian directly instructs you.",
      "Keep the packet so the drug and strength can be confirmed.",
      "Watch closely and call back immediately if any symptoms appear."
    ] : [
      "Phone your vet, an emergency animal hospital or a poison line immediately, before symptoms start.",
      "Take the packet or a clear photo of it and note the approximate time of exposure.",
      "Do not induce vomiting or give any medication unless a veterinarian directly instructs you.",
      "Start travelling to the clinic while speaking with the veterinary team when possible."
    ];
    this.actions.innerHTML = "";
    items.forEach((text) => {
      const li = document.createElement("li");
      li.textContent = text;
      this.actions.appendChild(li);
    });
    this.result.hidden = false;
    this.result.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  showError(message, element) {
    this.error.textContent = message;
    element?.focus();
  }

  clearError() { this.error.textContent = ""; }
  hideResult() { this.result.hidden = true; }

  restart() {
    this.form.reset();
    this.state = { species: "dog", drug: "ibuprofen", weightUnit: "kg", mode: "tablets" };
    this.querySelectorAll("[data-ipt-group]").forEach((group) => {
      group.querySelectorAll("[data-ipt-toggle]").forEach((button, index) => {
        button.classList.toggle("is-active", index === 0);
        button.setAttribute("aria-pressed", String(index === 0));
      });
    });
    this.renderMode();
    this.renderStrengthChips();
    this.clearError();
    this.hideResult();
    this.weightInput.focus();
  }
}
if (!customElements.get("s-ibuprofen-paracetamol-toxicity-calculator")) {
  customElements.define("s-ibuprofen-paracetamol-toxicity-calculator", SIbuprofenParacetamolToxicityCalculator);
}
